import { BackArrowIcon } from "@/components/atoms/BackArrowIcon";
import { ProgressBar } from "@/components/atoms/ProgressBar";
import { Typography } from "@/components/atoms/Typography";
import { useTranslation, useUIColors } from "@/core";
import { logger } from "@/core/logger";
import { useI18nContext } from "@/core/providers/I18nProvider";
import { useNavigation, usePathname } from "expo-router";
import React from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { BaseHeader } from "./BaseHeader";

export interface ProgressHeaderProps {
  showBackButton?: boolean;
  onBackPress?: (() => void) | undefined;
  showLanguageSwitch?: boolean;
  onLanguagePress?: ((language: "en" | "ar") => void) | undefined;
  disableBack?: boolean; // externally disable
  hideBackIfRoot?: boolean; // auto-hide when cannot go back
  progress: number; // 0..1
  color?: string; // color for back button, progress bar, and language button
  hideProgressBar?: boolean; // explicitly hide progress bar (e.g., when opened from profile)
}

export const ProgressHeader = React.memo<ProgressHeaderProps>(
  function ProgressHeader({
    showBackButton = true,
    onBackPress,
    showLanguageSwitch = true,
    onLanguagePress,
    disableBack = false,
    hideBackIfRoot = true,
    progress,
    color,
    hideProgressBar = false,
  }) {
    const pathname = usePathname();
    const navigation = useNavigation();
    const colors = useUIColors();
    const { currentLanguage, toggleLanguage } = useTranslation();
    const { isRTL } = useI18nContext();
    const isRoot = !pathname || pathname === "/";
    const navCanGoBack =
      typeof (navigation as any)?.canGoBack === "function"
        ? (navigation as any).canGoBack()
        : false;

    // Default back handler using navigation
    const handleDefaultBack = () => {
      if (navCanGoBack && (navigation as any)?.goBack) {
        try {
          (navigation as any).goBack();
        } catch {
          // Silently handle navigation errors (no screen to go back to)
          logger.warn("ProgressHeader", "Cannot go back - no previous screen");
        }
      }
    };

    // Determine if back button should show
    // If hideBackIfRoot is true, check if we can actually go back
    // Otherwise, show if onBackPress is provided or if we can go back
    const shouldShowBack =
      showBackButton &&
      !disableBack &&
      ((onBackPress && (!hideBackIfRoot || navCanGoBack)) || // Show if handler provided and (not hiding on root or can go back)
        !hideBackIfRoot ||
        (!isRoot && navCanGoBack)); // Show if not hiding on root or can actually go back

    // Use custom handler if provided, otherwise use default navigation
    const backHandler = onBackPress || handleDefaultBack;

    // Use provided color or default to fontPrimary for back button
    const backButtonColor = color || colors.fontPrimary;
    // Progress bar uses vectorsIconGreyscale per Figma design
    const progressBarColor = colors.vectorsIconGreyscale;

    // Language toggle handler
    const handleLanguageToggle = async () => {
      try {
        logger.info("ProgressHeader", "Toggling language", { currentLanguage });
        const newLanguage = await toggleLanguage();
        logger.info("ProgressHeader", "Language toggled successfully", { newLanguage });
        onLanguagePress?.(newLanguage);
      } catch (error) {
        logger.error("ProgressHeader", "Failed to toggle language:", error);
      }
    };

    // Render language button based on RTL state
    const renderLanguageButton = () => {
      if (!showLanguageSwitch) return null;

      // Show opposite language text: when English is selected, show Arabic; when Arabic is selected, show English
      // Position: English button on right when isRTL is false, Arabic button on left when isRTL is true
      const displayText = currentLanguage === "en" ? "العربية" : "English";

      return (
        <TouchableOpacity
          onPress={handleLanguageToggle}
          style={styles.languageButton}
          activeOpacity={0.7}
        >
          <Typography
            variant="body"
            numberOfLines={1}
            style={[
              styles.languageText,
              {
                color: backButtonColor,
              },
            ]}
          >
            {displayText}
          </Typography>
        </TouchableOpacity>
      );
    };

    return (
      <BaseHeader
        leading={
          <View style={styles.leadingContainer}>
            {shouldShowBack && (
              <TouchableOpacity
                onPress={backHandler}
                style={styles.buttonContainer}
              >
                <BackArrowIcon size={24} color={backButtonColor} />
              </TouchableOpacity>
            )}
          </View>
        }
        middle={
          hideProgressBar ? (
            <View style={styles.placeholder} />
          ) : (
            <View style={styles.progressBarContainer}>
              <ProgressBar progress={progress} color={progressBarColor} />
            </View>
          )
        }
        trailing={
          renderLanguageButton() || <View style={styles.placeholder} />
        }
      />
    );
  },
);

const styles = StyleSheet.create({
  leadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  buttonContainer: {
    height: 40,
    width: 40, // Figma: 40px width for back button
    justifyContent: "center",
  },
  progressBarContainer: {
    alignItems: "center",
    justifyContent: "center",
    width: 220, // Figma: 220px fixed width for progress bar
  },
  languageButton: {
    minHeight: 40,
    justifyContent: "center",
    paddingVertical: 4,
  },
  languageText: {
    fontWeight: "500",
    lineHeight: 20,
    includeFontPadding: false,
    textAlignVertical: "center",
  },
  placeholder: {
    height: 40,
    width: 40, // Figma: 40px width to match back button
  },
});

export default ProgressHeader;
